(() => {
  'use strict';

  const INITIAL_VISIBLE = 3;
  const PAGE_SIZE = 3;
  const ROOT_CLASS = 'wewok-doran-reviews';
  const HIDDEN_CLASS = 'wewok-review-card-hidden';
  const NATIVE_BUTTON_CLASS = 'wewok-native-review-load-more';
  const ACTIONS_CLASS = 'wewok-review-actions';
  const BUTTON_CLASS = 'wewok-review-view-more';
  const HEADING_TEXT = /^(rating\s*(?:&|and)\s*reviews|reviews(?:\s+for\b.*)?)$/i;
  const NATIVE_LOAD_TEXT = /^(view\s+more\s+reviews|load\s+more)$/i;

  let state = {
    root: null,
    list: null,
    heading: null,
    visibleCount: INITIAL_VISIBLE,
    observer: null,
    working: false
  };

  const normalize = (value) => (value || '').replace(/\s+/g, ' ').trim();

  function findHeading() {
    const headings = document.querySelectorAll(
      '#MainContent h1, #MainContent h2, #MainContent h3, #MainContent h4, #MainContent [role="heading"]'
    );

    return Array.from(headings).find((heading) => HEADING_TEXT.test(normalize(heading.textContent))) || null;
  }

  function findRoot(heading) {
    const dataRoot = heading.closest('[data-dr]');
    if (dataRoot) return dataRoot;

    let node = heading.parentElement;
    let fallback = node;

    while (node && node !== document.body) {
      const ratingIcons = node.querySelectorAll('.rating-icon-svg').length;
      const buttons = node.querySelectorAll('button').length;
      const textLength = normalize(node.textContent).length;

      if (ratingIcons >= 5 && buttons >= 1 && textLength > 80) fallback = node;
      if (node.tagName === 'SECTION' && ratingIcons >= 5) return node;

      node = node.parentElement;
    }

    return fallback || heading.parentElement;
  }

  function addCandidate(groups, element) {
    if (!element || !element.parentElement) return;

    const textLength = normalize(element.textContent).length;
    const iconCount = element.querySelectorAll('.rating-icon-svg').length;

    if (iconCount < 4 || iconCount > 6 || textLength < 20 || textLength > 3000) return;
    if (element.matches('button, [role="button"], form, header, nav')) return;

    const parent = element.parentElement;
    if (!groups.has(parent)) groups.set(parent, new Set());
    groups.get(parent).add(element);
  }

  function findReviewGroup(root) {
    const groups = new Map();

    root.querySelectorAll('.rating-icon-svg').forEach((icon) => {
      let element = icon.parentElement;
      let depth = 0;

      while (element && element !== root && depth < 9) {
        addCandidate(groups, element);
        element = element.parentElement;
        depth += 1;
      }
    });

    return Array.from(groups.entries())
      .map(([parent, elements]) => ({
        parent,
        cards: Array.from(elements).filter((element) => element.parentElement === parent)
      }))
      .filter((group) => group.cards.length >= 2)
      .map((group) => {
        const averageText = group.cards.reduce(
          (total, card) => total + normalize(card.textContent).length,
          0
        ) / group.cards.length;

        return { ...group, score: (group.cards.length * 1000) + Math.min(averageText, 800) };
      })
      .sort((a, b) => b.score - a.score)[0] || null;
  }

  function findNativeLoadButton(root) {
    const buttons = root.querySelectorAll('button, a[role="button"], input[type="button"]');

    return Array.from(buttons).find((button) => {
      if (button.classList.contains(BUTTON_CLASS)) return false;
      const label = normalize(button.textContent || button.value || button.getAttribute('aria-label'));
      return NATIVE_LOAD_TEXT.test(label);
    }) || null;
  }

  function ensureActionButton(root, list) {
    let actions = root.querySelector(`.${ACTIONS_CLASS}`);
    let button = root.querySelector(`.${BUTTON_CLASS}`);

    if (!actions) {
      actions = document.createElement('div');
      actions.className = ACTIONS_CLASS;
    }

    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = BUTTON_CLASS;
      button.addEventListener('click', handleAction);
      actions.appendChild(button);
    }

    if (!actions.isConnected) list.insertAdjacentElement('afterend', actions);
    return button;
  }

  function applyVisibility() {
    if (!state.root) return;

    const group = findReviewGroup(state.root);
    if (!group) return;

    state.list = group.parent;
    const cards = group.cards;
    const nativeButton = findNativeLoadButton(state.root);

    if (nativeButton) {
      nativeButton.classList.add(NATIVE_BUTTON_CLASS);
      nativeButton.setAttribute('aria-hidden', 'true');
      nativeButton.tabIndex = -1;
    }

    cards.forEach((card, index) => {
      const hidden = index >= state.visibleCount;
      card.classList.toggle(HIDDEN_CLASS, hidden);
      card.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    });

    const hasHiddenCards = cards.length > state.visibleCount;
    const hasMoreFromApp = Boolean(nativeButton);
    const canExpand = hasHiddenCards || hasMoreFromApp;
    const canCollapse = state.visibleCount > INITIAL_VISIBLE;
    const button = ensureActionButton(state.root, state.list);

    button.hidden = !(canExpand || canCollapse);
    button.disabled = state.working;

    if (state.working) {
      button.textContent = 'Loading…';
      button.dataset.action = 'loading';
      return;
    }

    if (!canExpand && canCollapse) {
      button.textContent = 'View Less';
      button.dataset.action = 'collapse';
      button.setAttribute('aria-expanded', 'true');
    } else {
      button.textContent = 'View More';
      button.dataset.action = 'expand';
      button.setAttribute('aria-expanded', canCollapse ? 'true' : 'false');
    }
  }

  function handleAction(event) {
    const button = event.currentTarget;
    if (state.working || !state.root) return;

    if (button.dataset.action === 'collapse') {
      state.visibleCount = INITIAL_VISIBLE;
      applyVisibility();
      if (state.heading) state.heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    const group = findReviewGroup(state.root);
    if (!group) return;

    if (group.cards.length > state.visibleCount) {
      state.visibleCount += PAGE_SIZE;
      applyVisibility();
      return;
    }

    const nativeButton = findNativeLoadButton(state.root);
    if (!nativeButton) {
      applyVisibility();
      return;
    }

    state.working = true;
    applyVisibility();

    const beforeCount = group.cards.length;
    nativeButton.click();

    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      const updatedGroup = findReviewGroup(state.root);
      const timedOut = Date.now() - startedAt > 7000;

      if ((updatedGroup && updatedGroup.cards.length > beforeCount) || timedOut) {
        window.clearInterval(timer);
        state.visibleCount += PAGE_SIZE;
        state.working = false;
        applyVisibility();
      }
    }, 250);
  }

  function initialise() {
    const heading = findHeading();
    if (!heading) return false;

    const root = findRoot(heading);
    if (!root) return false;

    if (state.root !== root) {
      if (state.observer) state.observer.disconnect();

      state = {
        root,
        list: null,
        heading,
        visibleCount: INITIAL_VISIBLE,
        observer: null,
        working: false
      };

      root.classList.add(ROOT_CLASS);
      heading.classList.add('wewok-review-heading');

      state.observer = new MutationObserver(() => {
        window.requestAnimationFrame(applyVisibility);
      });

      state.observer.observe(root, { childList: true, subtree: true });
    }

    applyVisibility();
    return true;
  }

  function boot() {
    if (initialise()) return;

    const pageObserver = new MutationObserver(() => {
      if (initialise()) pageObserver.disconnect();
    });

    pageObserver.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => pageObserver.disconnect(), 15000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', initialise);
})();
